<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useProjectStore } from '@/stores/ProjectStore'
import { useTaskStore } from '@/stores/TaskStore'
import { useWeekCanvasStore } from '@/stores/WeekCanvasStore'
import { useWeekReviewMetrics } from '@/composables/useWeekReviewMetrics'
import ExecutionRateWidget from '@/components/dashboard/weekly-review/ExecutionRateWidget.vue'
import EnergySyncWidget from '@/components/dashboard/weekly-review/EnergySyncWidget.vue'
import CapacityGrowLineWidget from '@/components/dashboard/weekly-review/CapacityGrowLineWidget.vue'
import ProjectAllocationWidget from '@/components/dashboard/weekly-review/ProjectAllocationWidget.vue'
import GoldenPathPanel from '@/components/dashboard/weekly-review/GoldenPathPanel.vue'

const projectStore = useProjectStore()
const taskStore = useTaskStore()
const weekCanvas = useWeekCanvasStore()

const { projects } = storeToRefs(projectStore)
const { tasks, loading } = storeToRefs(taskStore)
const energyBlocks = computed(() => weekCanvas.energyBlocks)
const fixedBlocks = computed(() => weekCanvas.fixedBlocks)

const metrics = useWeekReviewMetrics({
  tasks,
  projects,
  energyBlocks,
  fixedBlocks,
})

onMounted(async () => {
  document.querySelector('.layout-page-content')?.classList.add('weekly-review-fullbleed')

  if (!projects.value.length) await projectStore.fetchProjects()
  await Promise.all([
    taskStore.fetchTasks({ include_completed: true, include_archived: true }),
    weekCanvas.fetchGoldenDays({ year: new Date().getFullYear() }),
  ])
})

onBeforeUnmount(() => {
  document.querySelector('.layout-page-content')?.classList.remove('weekly-review-fullbleed')
})
</script>

<template>
  <div class="weekly-review-page">
    <header class="weekly-review-header">
      <div>
        <div class="text-overline text-primary mb-1">{{ $t('dashboard.weeklyReview.overline') }}</div>
        <h1 class="weekly-review-title">
          <VIcon icon="mdi-chart-box-outline" size="30" />
          {{ $t('dashboard.weeklyReview.title') }}
        </h1>
        <p class="weekly-review-subtitle">
          {{ $t('dashboard.weeklyReview.subtitle') }}
        </p>
      </div>

      <div class="weekly-review-summary">
        <VChip color="primary" variant="tonal">
          <VIcon icon="mdi-calendar-week-outline" start />
          {{ $t('dashboard.weeklyReview.thisWeek') }}
        </VChip>
        <VChip color="success" variant="tonal">
          <VIcon icon="mdi-clock-check-outline" start />
          {{ metrics.usableTime.value.usableHours.toFixed(0) }}h usable
        </VChip>
      </div>
    </header>

    <div v-if="loading && !tasks.length" class="weekly-review-loading">
      <VProgressCircular indeterminate color="primary" size="42" />
      <span>{{ $t('dashboard.weeklyReview.loading') }}</span>
    </div>

    <template v-else>
      <section class="weekly-review-grid">
        <ExecutionRateWidget :metric="metrics.executionRate.value" />
        <EnergySyncWidget :metric="metrics.energySync.value" />
        <CapacityGrowLineWidget
          :points="metrics.capacityGrowLine.value"
          :usable-hours="metrics.usableTime.value.usableHours"
          class="weekly-review-wide"
        />
        <ProjectAllocationWidget :allocation="metrics.projectAllocation.value" />
      </section>

      <GoldenPathPanel class="mt-4" :golden-days="weekCanvas.goldenDaySet" />
    </template>
  </div>
</template>

<style scoped lang="scss">
.weekly-review-page {
  min-block-size: calc(100vh - 64px);
  padding: 22px 24px;
  background:
    radial-gradient(circle at 14% 8%, rgba(var(--v-theme-primary), 0.14), transparent 26%),
    radial-gradient(circle at 86% 12%, rgba(var(--v-theme-info), 0.12), transparent 24%),
    linear-gradient(135deg, #070b12 0%, #0d1117 48%, #080a10 100%);
}

.weekly-review-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-block-end: 18px;
}

.weekly-review-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 1.65rem;
  font-weight: 750;
  letter-spacing: -0.03em;
}

.weekly-review-subtitle {
  max-inline-size: 760px;
  margin: 4px 0 0;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.92rem;
}

.weekly-review-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.weekly-review-loading {
  display: grid;
  min-block-size: 420px;
  place-items: center;
  gap: 12px;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.weekly-review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.weekly-review-wide {
  grid-column: span 2;
}

@media (max-width: 1100px) {
  .weekly-review-grid,
  .weekly-review-wide {
    grid-template-columns: 1fr;
    grid-column: auto;
  }
}

@media (max-width: 720px) {
  .weekly-review-page {
    padding: 14px;
  }
}
</style>

<style lang="scss">
.layout-page-content.weekly-review-fullbleed {
  max-inline-size: none !important;
  padding-inline: 0 !important;
  padding-block: 0 !important;
}
</style>
